/** The outputs of a menu: print, PDF, Word (docs/01-architektur.md, Ausgaben und Vorlagen). */
import { CHOICE_TEXT, OR_TEXT, cardWords, viewForPrint, entranceDigest, entranceMenuOf, linksLine, namedDesignFor, menuView, phraseIn, scaleDesign, viewIn } from "@speisekarte/core";
import type { CardLang, Design, MenuInput, PdfSlot } from "@speisekarte/core";
import { fileName, smallHead } from "./menuHtml.ts";
import type { PrintMenu } from "./menuHtml.ts";
import { downloadBlob, pdfTab, printInTab, printPdfPages } from "./print.ts";
import { heldLogo, loadLogo } from "./logo.ts";
import { ownPageFiles } from "./own-pages.ts";
import { api, dishesById, store, textsById } from "./store.ts";
import type { Data } from "./store.ts";
import { timedOn } from "./timing.ts";
import type { WordStyles } from "./menuDocx.ts";

/**
 * The menu to print, with the logo when the device has it (logo.ts keeps it there); set with its
 * style at the menu's scale (T14). The recommendations on the net (S37, L3) print as an insert: a
 * small head over their own legend, the menu in the folder keeps the large one. `id` names the menu
 * when `menu` is a draft without it. In a further language (S36) the dishes' translations, instead
 * of the German or below it; „oder“ and „zur Wahl“ too while the style keeps the German default.
 */
/** The house's own footer in the card's language (S36), from its dictionary; both on a bilingual card. */
function footerIn(data: Data, lang: CardLang | null): string {
  const own = data.settings.footer;
  if (!lang || !own) return own;
  const t = phraseIn(own, lang.lang, data.phrases ?? {});
  return t === own ? own : lang.both ? `${own} / ${t}` : t;
}

export function printMenuOf(menu: MenuInput, data: Data, date = new Date(), design?: Design, id?: string, lang: CardLang | null = null): PrintMenu {
  const named = namedDesignFor(data.designs, menu.style ?? "");
  const insert = isInsert(data, id ?? (menu as { id?: string }).id);
  const scaled = scaleDesign(design ?? named.design, menu.scale, menu.layout === "columns" || menu.drinks?.layout === "columns");
  const words = cardWords({ ...(lang ? { lang } : {}) });
  const worded = lang ? { ...scaled, orText: scaled.orText === OR_TEXT.fallback ? words.or : scaled.orText, choiceText: scaled.choiceText === CHOICE_TEXT.fallback ? words.choice : scaled.choiceText } : scaled;
  const d = insert ? smallHead(worded) : worded;
  // The list at the entrance (S42): the catalogue's prices; its print is noted (German only, as it hangs).
  const entrance = isEntrance(data, id ?? (menu as { id?: string }).id);
  const view = menuView(entrance ? entranceMenuOf(menu, dishesById(data)) : menu, dishesById(data), data.allergens, textsById(data), data.tagIcons);
  return { ...(entrance && !lang ? { entrance: entranceDigest(view) } : {}), view: viewForPrint(viewIn(view, dishesById(data), lang, data.phrases ?? {}), lang, timedOn(data)), house: data.settings.house || data.workspace, date, footer: footerIn(data, lang), logo: d.logoHidden ? null : heldLogo(data), ...(d.footLogo ? { footLogo: heldLogo(data) } : {}), logoOnly: d.logoOnly, logoPlace: d.logoPlace, design: d, style: { id: named.id, name: named.name }, ...(insert ? { small: true as const } : {}), ...(!insert && menu.ownPages ? { ownPages: menu.ownPages } : {}), ...(data.settings.linksInPrint && linksLine(data.settings) ? { links: linksLine(data.settings) } : {}) };
}

/** Whether a menu is the price list beside the entrance (S42). */
export const isEntrance = (data: Data, id: string | undefined) => Boolean(id) && id === data.settings.entranceMenu;

/** What the list at the entrance shows now (S42), to compare with what was printed; null: none. */
export function entranceNow(data: Data): string | null {
  const menu = data.menus.find((m) => m.id === data.settings.entranceMenu);
  return menu ? entranceDigest(menuView(entranceMenuOf(menu, dishesById(data)), dishesById(data), data.allergens, textsById(data), data.tagIcons)) : null;
}

/** Printed, as PDF or Word: the list at the entrance notes it (S42); a failure there costs the print nothing. */
async function noted(m: PrintMenu) {
  if (!m.entrance) return;
  await store.change(() => api("/api/entrance/printed", { body: { digest: m.entrance } })).catch(() => undefined);
}

/** Whether a menu is the recommendations above the menu on the net (S37), printed as an insert. */
export const isInsert = (data: Data, id: string | undefined) => Boolean(id) && Boolean(data.published) && id === data.settings.recommendationsMenu && id !== data.published!.menu;


/** For a file there is time to fetch the logo first. */
/**
 * The logo fetched when the device did not have it yet: in the head unless the style says „Ohne
 * Logo“ (that was fetched regardless until 28.10.2026), in the foot where the style has it there (S43).
 */
const withLogo = async (m: PrintMenu, data: Data): Promise<PrintMenu> => {
  const head = !m.design?.logoHidden && !m.logo;
  const foot = Boolean(m.design?.footLogo) && !m.footLogo;
  if (!data.logo || (!head && !foot)) return m;
  const logo = await loadLogo(data);
  return { ...m, ...(head ? { logo } : {}), ...(foot ? { footLogo: logo } : {}) };
};

/** The files of the own pages the menu takes (S50), fetched when it has any; an insert never (S37). */
const withOwn = async (m: PrintMenu, data: Data): Promise<PrintMenu> => {
  if (!m.ownPages || m.small) return m;
  const own = await ownPageFiles({ ownPages: m.ownPages }, data);
  return own.front.length || own.back.length ? { ...m, own } : m;
};

/** Everything a file needs that the device may not have yet: the logo, the own pages. */
const complete = async (m: PrintMenu, data: Data) => withOwn(await withLogo(m, data), data);

const pdfOf = async (m: PrintMenu) => (await import("./menuPdf.ts")).menuPdf(m);

/** Printed from the PDF, so the paper shows exactly what the PDF shows; Safari prints it in a tab. */
export async function printMenu(m: PrintMenu, data: Data) {
  // Opened right in the tap, before anything waits (S18).
  const tab = pdfTab();
  try {
    const full = await complete(m, data);
    if (tab) await printInTab(tab, await pdfOf(full));
    else await printPdfPages({ name: fileName(full, "pdf"), make: () => pdfOf(full) });
    await noted(m);
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/**
 * Any PDF printed as the menu is (S18): in Safari in a tab of its own (opened right in the tap),
 * else its pages as pictures; on an iPhone's home screen the share sheet. The QR stickers.
 */
export async function printPdf(name: string, make: () => Promise<Uint8Array>) {
  const tab = pdfTab();
  try {
    if (tab) return printInTab(tab, await make());
    await printPdfPages({ name, make });
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/** The PDF as „PDF herunterladen“ makes it, with logo and own pages (S50): for a slot on the net (S54). */
export async function pdfBytes(m: PrintMenu, data: Data): Promise<Uint8Array> {
  return pdfOf(await complete(m, data));
}

/** A menu as PDF into a slot (S54): its address stays, the PDF in it is this one now; with its mark and language (S62, Z-0: `pdfMarkOf`). */
export async function publishPdfSlot(m: PrintMenu, data: Data, slot: string, menuId: string | undefined, mark: { digest: string; lang: string }): Promise<PdfSlot> {
  const bytes = await pdfBytes(m, data);
  const query = new URLSearchParams({ ...(menuId ? { menu: menuId } : {}), digest: mark.digest, lang: mark.lang });
  const r = await store.change(() => api<{ slot: PdfSlot }>(`/api/pdf-slots/${slot}/pdf?${query}`, { method: "PUT", pdf: new Blob([bytes as BlobPart], { type: "application/pdf" }), timeout: 300_000 }));
  return r.slot;
}

/** A menu as PDF into a slot at a moment (S62 § 10): made now, kept until then; with its mark and language. */
export async function planPdfSlot(m: PrintMenu, data: Data, slot: string, menuId: string, at: number, mark: { digest: string; lang: string }): Promise<void> {
  const bytes = await pdfBytes(m, data);
  const query = new URLSearchParams({ at: String(at), menu: menuId, digest: mark.digest, lang: mark.lang });
  await store.change(() => api(`/api/pdf-slots/${slot}/plan?${query}`, { method: "PUT", pdf: new Blob([bytes as BlobPart], { type: "application/pdf" }), timeout: 300_000 }));
}

export async function downloadPdf(m: PrintMenu, data: Data) {
  const full = await complete(m, data);
  downloadBlob(new Blob([(await pdfOf(full)) as BlobPart], { type: "application/pdf" }), fileName(full, "pdf"));
  await noted(m);
}

/** The own Word template of a style (S21), fetched once per upload (they can be large). */
const styles = new Map<string, { at: number; word: WordStyles | null }>();
async function ownStyles(data: Data, style: string | undefined): Promise<WordStyles | null> {
  const held = data.word.find((w) => w.style === style);
  if (!held || !style) return null;
  const kept = styles.get(style);
  if (kept?.at === held.at) return kept.word;
  try {
    const { word } = await api<{ word: WordStyles | null }>(`/api/word-styles/${encodeURIComponent(style)}`);
    styles.set(style, { at: held.at, word });
    return word;
  } catch {
    // Offline: the style makes its own, so the file still comes.
    return null;
  }
}

export async function downloadWord(m: PrintMenu, data: Data) {
  const { menuDocx } = await import("./menuDocx.ts");
  downloadBlob(await menuDocx(await complete(m, data), await ownStyles(data, m.style?.id)), fileName(m, "docx"));
  await noted(m);
}
